import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { http, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Gift, Mail, User, Heart, CreditCard, DollarSign, Send } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [25, 50, 100, 250];

export default function GiftCards() {
  const { user } = useAuth();
  const settings = useSiteSettings();
  const logoUrl = settings.site_logo_url
    ? (settings.site_logo_url.startsWith("http") ? settings.site_logo_url : `${API_BASE}${settings.site_logo_url}`)
    : null;
  const nav = useNavigate();
  const [amount, setAmount] = useState(50);
  const [customMode, setCustomMode] = useState(false);
  const [purchaserName, setPurchaserName] = useState(user?.name || "");
  const [purchaserEmail, setPurchaserEmail] = useState(user?.email || "");
  const [recipientMode, setRecipientMode] = useState("direct");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount < 10 || numAmount > 500) {
      toast.error("Amount must be between $10 and $500"); return;
    }
    if (!purchaserName || !purchaserEmail) { toast.error("Add your name and email"); return; }
    if (recipientMode === "direct" && (!recipientName || !recipientEmail)) {
      toast.error("Recipient name and email are required when sending direct"); return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/gift-cards/purchase", {
        amount: numAmount,
        purchaser_name: purchaserName,
        purchaser_email: purchaserEmail,
        recipient_mode: recipientMode,
        recipient_name: recipientName || null,
        recipient_email: recipientEmail || null,
        personal_message: personalMessage || null,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      });
      if (paymentMethod === "stripe" && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        nav(`/gift-cards/thank-you?code=${data.code}&method=${paymentMethod}&amount=${numAmount}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start gift card purchase");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="bg-black pt-24 pb-12">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-10">
            <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-3">Give a Simple Truth</div>
            <h1 className="font-display-saira text-5xl md:text-7xl text-[#F5F2EB] tracking-tight" data-testid="gift-card-h1">DIGITAL GIFT CARD</h1>
            <p className="font-body text-[#9a9690] mt-5 max-w-xl mx-auto">A truth, ready to be carried. Pick any amount from $10 to $500 — your recipient gets a beautifully designed card the moment you check out.</p>
          </div>

          <form onSubmit={submit} className="grid lg:grid-cols-5 gap-8 mt-10" data-testid="gift-card-form">
            {/* === LEFT: form === */}
            <div className="lg:col-span-3 space-y-6">

              {/* Amount */}
              <fieldset className="border border-white/10 bg-[#0a0a0a] p-5">
                <legend className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] px-2">Amount</legend>
                <div className="flex flex-wrap gap-3 mt-2">
                  {PRESETS.map(p => (
                    <button key={p} type="button"
                      data-testid={`amount-preset-${p}`}
                      onClick={() => { setAmount(p); setCustomMode(false); }}
                      className={`px-5 py-3 border font-display-saira text-2xl transition-colors ${amount === p && !customMode ? "border-[#C9A055] bg-[#C9A055]/15 text-[#C9A055]" : "border-white/10 text-[#F5F2EB] hover:border-white/30"}`}>
                      ${p}
                    </button>
                  ))}
                  <button type="button"
                    data-testid="amount-custom-toggle"
                    onClick={() => setCustomMode(true)}
                    className={`px-5 py-3 border font-ui text-[11px] tracking-[0.22em] uppercase transition-colors ${customMode ? "border-[#C9A055] bg-[#C9A055]/15 text-[#C9A055]" : "border-white/10 text-[#F5F2EB] hover:border-white/30"}`}>
                    Custom
                  </button>
                </div>
                {customMode && (
                  <div className="mt-4 relative max-w-xs">
                    <DollarSign size={18} className="absolute left-3 top-3.5 text-[#9a9690]"/>
                    <input data-testid="amount-custom-input" type="number" min="10" max="500" step="1"
                      value={amount} onChange={e => setAmount(e.target.value)}
                      placeholder="Any amount $10 – $500"
                      className="stc-input pl-10 font-display-saira text-2xl text-[#C9A055]"/>
                  </div>
                )}
              </fieldset>

              {/* From */}
              <fieldset className="border border-white/10 bg-[#0a0a0a] p-5">
                <legend className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] px-2">From you</legend>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  <label className="block">
                    <span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Your name</span>
                    <input data-testid="purchaser-name" value={purchaserName} onChange={e => setPurchaserName(e.target.value)} className="stc-input mt-1.5" placeholder="Mickey Trivett" required/>
                  </label>
                  <label className="block">
                    <span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Your email</span>
                    <input data-testid="purchaser-email" type="email" value={purchaserEmail} onChange={e => setPurchaserEmail(e.target.value)} className="stc-input mt-1.5" placeholder="you@example.com" required/>
                  </label>
                </div>
              </fieldset>

              {/* Delivery */}
              <fieldset className="border border-white/10 bg-[#0a0a0a] p-5">
                <legend className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] px-2">Delivery</legend>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button type="button"
                    data-testid="recipient-direct-btn"
                    onClick={() => setRecipientMode("direct")}
                    className={`px-4 py-4 border text-left transition-colors ${recipientMode === "direct" ? "border-[#C9A055] bg-[#C9A055]/10 text-[#F5F2EB]" : "border-white/10 text-[#9a9690] hover:border-white/30"}`}>
                    <Mail size={16} className={recipientMode === "direct" ? "text-[#C9A055]" : ""}/>
                    <div className="font-ui text-[11px] tracking-[0.22em] uppercase mt-2">Send to recipient</div>
                    <div className="font-body text-[12px] text-[#737373] mt-1">They get the card by email instantly.</div>
                  </button>
                  <button type="button"
                    data-testid="recipient-self-btn"
                    onClick={() => setRecipientMode("self")}
                    className={`px-4 py-4 border text-left transition-colors ${recipientMode === "self" ? "border-[#C9A055] bg-[#C9A055]/10 text-[#F5F2EB]" : "border-white/10 text-[#9a9690] hover:border-white/30"}`}>
                    <User size={16} className={recipientMode === "self" ? "text-[#C9A055]" : ""}/>
                    <div className="font-ui text-[11px] tracking-[0.22em] uppercase mt-2">Send to me</div>
                    <div className="font-body text-[12px] text-[#737373] mt-1">I&apos;ll print or forward it myself.</div>
                  </button>
                </div>
                {recipientMode === "direct" && (
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <label className="block">
                      <span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Recipient name</span>
                      <input data-testid="recipient-name" value={recipientName} onChange={e => setRecipientName(e.target.value)} className="stc-input mt-1.5" placeholder="Jane Smith" required/>
                    </label>
                    <label className="block">
                      <span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Recipient email</span>
                      <input data-testid="recipient-email" type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} className="stc-input mt-1.5" placeholder="jane@example.com" required/>
                    </label>
                  </div>
                )}
                <label className="block mt-4">
                  <span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690]">Personal note <span className="text-[#5a5650]">(optional)</span></span>
                  <textarea data-testid="personal-message" rows={3} maxLength={220} value={personalMessage} onChange={e => setPersonalMessage(e.target.value)} className="stc-input mt-1.5 font-body" placeholder="Carry this truth wherever you go…"/>
                  <span className="block text-right font-ui text-[10px] text-[#5a5650] mt-1">{personalMessage.length}/220</span>
                </label>
              </fieldset>

              {/* Payment */}
              <fieldset className="border border-white/10 bg-[#0a0a0a] p-5">
                <legend className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] px-2">Payment</legend>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[
                    { v: "stripe",  label: "Card", icon: CreditCard, sub: "Instant" },
                    { v: "cashapp", label: "Cash App", icon: DollarSign, sub: "Manual confirm" },
                    { v: "venmo",   label: "Venmo", icon: Send, sub: "Manual confirm" },
                  ].map(({ v, label, icon: I, sub }) => (
                    <button key={v} type="button"
                      data-testid={`pay-${v}`}
                      onClick={() => setPaymentMethod(v)}
                      className={`px-4 py-4 border text-center transition-colors ${paymentMethod === v ? "border-[#C9A055] bg-[#C9A055]/10 text-[#F5F2EB]" : "border-white/10 text-[#9a9690] hover:border-white/30"}`}>
                      <I size={18} className={`mx-auto ${paymentMethod === v ? "text-[#C9A055]" : ""}`}/>
                      <div className="font-ui text-[11px] tracking-[0.22em] uppercase mt-2">{label}</div>
                      <div className="font-ui text-[10px] text-[#737373] mt-0.5">{sub}</div>
                    </button>
                  ))}
                </div>
              </fieldset>

              <button type="submit"
                data-testid="gift-card-submit"
                disabled={busy}
                className="btn-gold w-full text-base">
                <Gift size={18}/> {busy ? "Processing…" : `Send a $${Number(amount || 0).toFixed(0)} gift card`}
              </button>
            </div>

            {/* === RIGHT: live preview === */}
            <div className="lg:col-span-2 lg:sticky lg:top-24 h-fit">
              <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-3">Preview</div>
              <div className="relative aspect-[1500/900] bg-black border-2 border-[#C9A055] overflow-hidden" data-testid="gift-card-preview">
                <div className="absolute inset-2 border border-[#C9A055]/40 pointer-events-none"/>
                <div className="absolute top-5 right-5 font-display-saira text-5xl md:text-6xl text-[#F5F2EB] leading-none">${Number(amount || 0).toFixed(0).replace(".00", "")}</div>
                <div className="absolute top-[55%] right-5 font-ui text-[10px] text-[#9a9690]">gift value</div>
                <div className="absolute top-5 left-5 w-20 h-20 flex items-center justify-center">
                  {logoUrl ? (
                    <img src={logoUrl} alt="STC" className="w-full h-full object-contain" data-testid="preview-logo"/>
                  ) : (
                    <div className="w-20 h-20 rounded-full border-2 border-[#C9A055] flex items-center justify-center">
                      <span className="font-display-saira text-base tracking-[0.18em] text-[#C9A055]">STC</span>
                    </div>
                  )}
                </div>
                <div className="absolute left-5 bottom-12 right-5">
                  <div className="font-ui text-[9px] tracking-[0.32em] uppercase text-[#C9A055] mb-2">From {purchaserName || "Your name"}</div>
                  {recipientMode === "direct" && recipientName && (
                    <div className="font-display-saira text-lg text-[#F5F2EB]">For {recipientName.toUpperCase()}</div>
                  )}
                  {personalMessage && <div className="font-script text-sm text-[#9a9690] mt-1 italic line-clamp-2">&ldquo;{personalMessage}&rdquo;</div>}
                </div>
                <div className="absolute bottom-3 left-5 right-5 font-ui text-[10px] text-[#5a5650] flex justify-between gap-3">
                  <span>simpletruthsclub.com</span>
                  <span>Code unlocks after purchase</span>
                </div>
              </div>
              <div className="mt-4 border border-white/10 bg-[#0a0a0a] p-4 text-[12px] text-[#9a9690] font-body">
                <div className="flex items-center gap-2 text-[#F5F2EB] mb-2"><Heart size={14} className="text-[#C9A055]"/> What they receive</div>
                <ul className="space-y-1 list-disc list-inside">
                  <li>The full STC branded gift card image</li>
                  <li>A 16-character redemption code</li>
                  <li>One-click &ldquo;Redeem at checkout&rdquo; or &ldquo;Load to account&rdquo;</li>
                  <li>2-year expiration · partial use supported</li>
                </ul>
              </div>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}
